import { useCallback, useMemo } from 'react'

import get from 'lodash-es/get'
import max from 'lodash-es/max'
import reduce from 'lodash-es/reduce'

import { useOnboarding } from '@queries/onboarding'
import { queryClient } from '@store/data/queryClient'

export const useProgress = <T extends string>(
  moduleName?: T | '',
  {
    defaultModuleName = '',
    modulesNames,
    additionalProperties = null,
    refetchOnMount = true,
  }: {
    defaultModuleName?: T | ''
    modulesNames?: T[]
    additionalProperties?: Record<string, unknown> | null
    refetchOnMount?: boolean | 'always'
  } = {}
) => {
  const {
    data: onboardingData,
    queryKey: onboardingQueryKey,
    saveOnboarding,
    saveOnboardingState,
    isLoading,
    isRefetching,
  } = useOnboarding({ refetchOnMount })
  const saveProgress = useCallback(
    ({
      step,
      moduleName,
      length,
      isFinished = false,
    }: {
      step: number
      moduleName: string
      length: number
      isFinished?: boolean
    }) => {
      const onboardingData = queryClient.getQueryData(onboardingQueryKey) || {}
      const isCompleted = isFinished || step >= length
      let completion = isCompleted ? 100 : 0
      if (!isCompleted && step) {
        completion = Number(((100 / length - 1) * step).toFixed())
      }

      let progressStep = 0

      if (!isCompleted) {
        progressStep =
          max([
            // Just in case we have old, bad data that is not a number...
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
            +get(onboardingData, `${moduleName}.additionalProgress.step`, 0),
            step,
          ]) || 0
      }

      const data: Record<string, unknown> = {
        [moduleName]: {
          completion,
          additionalProperties: {
            ...additionalProperties,
            step: progressStep,
            length,
            lastUsed: true,
          },
        },
      }

      return saveOnboarding(data as any)
    },
    [onboardingQueryKey, saveOnboarding, additionalProperties]
  )

  const data = moduleName
    ? get(onboardingData, moduleName, onboardingData)
    : onboardingData

  const lastUsedModule = reduce(
    onboardingData,
    (acc, progressData, moduleName) =>
      get(progressData, 'additionalProperties.lastUsed', false)
        ? (moduleName as T)
        : acc,
    defaultModuleName
  )

  // `Partial` because the modules present depend on what the BE has stored, and
  // stays `undefined` until onboarding data arrives — consumers such as the
  // business-settings banner key off that to stay hidden while loading.
  const allCompletion: Partial<Record<T, number>> | undefined = onboardingData
    ? reduce<T, Partial<Record<T, number>>>(
        modulesNames,
        (acc, mName) => ({
          ...acc,
          [mName]: get(onboardingData, `${mName}.completion`, 0),
        }),
        {}
      )
    : undefined

  const isCompleted = useMemo(
    () =>
      !!allCompletion &&
      !(modulesNames ?? []).filter((mName) => (allCompletion[mName] ?? 0) < 100)
        .length,
    [allCompletion, modulesNames]
  )

  return {
    data,
    fullData: onboardingData,

    allCompletion,
    isCompleted,

    lastUsedModule,

    saveProgress,
    saveProgressState: saveOnboardingState,
    isLoading,
    isRefetching,
  }
}
